import type { BossId, VesselId } from '@bioforge/protocol';
import { ADENO, CORONA, ECOLI, GRIPPOZAVR, SPORNITSA } from './mockup.js';

/**
 * Черновые иллюстрации (SPEC.md, 19): оригинальные персонажи из простых фигур SVG.
 * Каждая функция рисует фигуру в квадрате 200 × 200; сцены собирают их через `place`.
 * Финальная графика с макетов заменит эти функции без изменения данных сцен.
 */

export const INK = '#120b2a';
export const MICROBE_COLORS = ['#9b6bff', '#ff4f7b', '#ffd23a'] as const;

/** Фигура в квадрате 200 × 200, сдвинутая в (x, y) и масштабированная. */
export function place(art: string, x: number, y: number, scale = 1, flip = false): string {
  const mirror = flip ? ` translate(${200 * scale} 0) scale(-1 1)` : '';
  return `<g transform="translate(${x} ${y})${mirror} scale(${scale})">${art}</g>`;
}

/** Отдельная картинка: квадрат 200 × 200 или полотно сцены. */
export function svg(content: string, width = 200, height = 200): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" role="img" aria-hidden="true">${content}</svg>`;
}

type Mood = 'angry' | 'goofy' | 'smirk' | 'sleepy' | 'evil' | 'dizzy';

/** Мультяшное лицо: глаза и рот по настроению. */
export function face(cx: number, cy: number, size: number, mood: Mood): string {
  const e = size * 0.32;
  const r = size * 0.13;
  const eye = (x: number): string => {
    if (mood === 'sleepy') {
      return `<path d="M${x - r} ${cy} q${r} ${r * 0.8} ${2 * r} 0" stroke="${INK}" stroke-width="${r * 0.45}" fill="none" stroke-linecap="round"/>`;
    }
    if (mood === 'dizzy') {
      const d = r * 0.8;
      return `<path d="M${x - d} ${cy - d} l${2 * d} ${2 * d} m0 ${-2 * d} l${-2 * d} ${2 * d}" stroke="${INK}" stroke-width="${r * 0.4}" stroke-linecap="round"/>`;
    }
    const pupil = mood === 'goofy' ? r * 0.45 : r * 0.55;
    const dx = mood === 'goofy' ? (x < cx ? -r * 0.35 : r * 0.35) : 0;
    return `<circle cx="${x}" cy="${cy}" r="${r}" fill="#fff" stroke="${INK}" stroke-width="${r * 0.3}"/><circle cx="${x + dx}" cy="${cy + r * 0.15}" r="${pupil}" fill="${INK}"/>`;
  };
  const brows =
    mood === 'angry' || mood === 'evil'
      ? `<path d="M${cx - e - r * 1.2} ${cy - r * 1.9} L${cx - e + r * 1.1} ${cy - r * 1.1} M${cx + e + r * 1.2} ${cy - r * 1.9} L${cx + e - r * 1.1} ${cy - r * 1.1}" stroke="${INK}" stroke-width="${r * 0.5}" stroke-linecap="round"/>`
      : '';
  const my = cy + size * 0.32;
  const w = size * 0.3;
  let mouth: string;
  switch (mood) {
    case 'angry':
      mouth = `<path d="M${cx - w} ${my + w * 0.3} Q${cx} ${my - w * 0.4} ${cx + w} ${my + w * 0.3} Z" fill="${INK}"/><path d="M${cx - w * 0.6} ${my + w * 0.1} l${w * 0.2} ${w * 0.25} l${w * 0.2} ${-w * 0.3} l${w * 0.2} ${w * 0.3} l${w * 0.2} ${-w * 0.3} l${w * 0.2} ${w * 0.25}" fill="#fff"/>`;
      break;
    case 'evil':
      mouth = `<path d="M${cx - w * 1.3} ${my - w * 0.2} Q${cx} ${my + w * 1.4} ${cx + w * 1.3} ${my - w * 0.2} Z" fill="${INK}"/><path d="M${cx - w} ${my} l${w * 0.25} ${w * 0.4} l${w * 0.25} ${-w * 0.35} l${w * 0.25} ${w * 0.4} l${w * 0.25} ${-w * 0.35} l${w * 0.25} ${w * 0.4} l${w * 0.25} ${-w * 0.35} l${w * 0.25} ${w * 0.4} l${w * 0.25} ${-w * 0.35}" fill="#fff"/>`;
      break;
    case 'goofy':
      mouth = `<ellipse cx="${cx}" cy="${my}" rx="${w * 0.6}" ry="${w * 0.7}" fill="${INK}"/><ellipse cx="${cx}" cy="${my + w * 0.35}" rx="${w * 0.35}" ry="${w * 0.25}" fill="#ff8fab"/>`;
      break;
    case 'smirk':
      mouth = `<path d="M${cx - w} ${my} Q${cx} ${my + w * 0.6} ${cx + w} ${my - w * 0.4}" stroke="${INK}" stroke-width="${r * 0.5}" fill="none" stroke-linecap="round"/>`;
      break;
    case 'sleepy':
      mouth = `<ellipse cx="${cx}" cy="${my}" rx="${w * 0.3}" ry="${w * 0.2}" fill="${INK}"/>`;
      break;
    case 'dizzy':
      mouth = `<path d="M${cx - w} ${my} q${w / 2} ${-w * 0.4} ${w} 0 t${w} 0" stroke="${INK}" stroke-width="${r * 0.45}" fill="none" stroke-linecap="round"/>`;
      break;
  }
  return eye(cx - e) + eye(cx + e) + brows + mouth;
}

const ring = (n: number, radius: number, cx: number, cy: number) =>
  Array.from({ length: n }, (_, i) => {
    const a = (i / n) * Math.PI * 2;
    return { x: cx + Math.cos(a) * radius, y: cy + Math.sin(a) * radius, a };
  });

/** Микробы макета: 0 — Коронавирус, 1 — Аденовирус, 2 — Кишечная палочка (SPEC.md, 15.1). */
export function microbe(kind: number): string {
  const body = [CORONA, ADENO, ECOLI][kind] ?? CORONA;
  return `<svg x="8" y="8" width="184" height="184" viewBox="0 0 24 24" overflow="visible">${body}</svg>`;
}

/** Микроб в «Биоплёнке» — полупрозрачный шестигранник. */
export function biofilm(inner: string, gold = false): string {
  const points = ring(6, 92, 100, 100)
    .map((p) => `${p.x},${p.y}`)
    .join(' ');
  const color = gold ? '#ffcf5a' : '#bdf3ff';
  return `${place(inner, 25, 25, 0.75)}<polygon points="${points}" fill="${color}" fill-opacity=".22" stroke="${color}" stroke-width="5" stroke-opacity=".8"/>`;
}

export function boss(id: BossId): string {
  const mockup = (body: string) =>
    `<svg x="4" y="4" width="192" height="192" viewBox="0 0 48 48" overflow="visible">${body}</svg>`;
  switch (id) {
    case 'spornitsa':
      return mockup(SPORNITSA(false));
    case 'grippozavr':
      return mockup(GRIPPOZAVR(false));
    case 'vibrion': {
      const tentacles = [0, 1, 2, 3]
        .map(
          (i) =>
            `<path d="M${70 + i * 18} 150 q${-14 + i * 6} 20 ${-4 + i * 6} 44" stroke="#2fbf71" stroke-width="9" fill="none" stroke-linecap="round"/>`,
        )
        .join('');
      return `${tentacles}<path d="M40 110 C40 40 150 20 170 80 C182 118 150 160 104 156 C70 154 52 140 40 110 Z" fill="#4fc3f7" stroke="${INK}" stroke-width="5"/><path d="M168 82 q24 -20 22 -54" stroke="#2fbf71" stroke-width="8" fill="none" stroke-linecap="round"/>${face(104, 96, 84, 'angry')}<circle cx="34" cy="60" r="7" fill="none" stroke="#bdf3ff" stroke-width="3"/><circle cx="22" cy="40" r="5" fill="none" stroke="#bdf3ff" stroke-width="3"/>`;
    }
    case 'tuberkul':
      return `<rect x="46" y="16" width="108" height="172" rx="54" fill="#e6d3a3" stroke="${INK}" stroke-width="5"/><path d="M60 40 q-4 30 4 50 M140 52 q6 24 -2 46 M70 150 q10 10 24 8" stroke="#c9b27a" stroke-width="6" fill="none" stroke-linecap="round"/><path d="M58 30 q8 -14 22 -12" stroke="#fff" stroke-opacity=".7" stroke-width="6" fill="none" stroke-linecap="round"/><path d="M150 120 q4 18 -2 30 q-6 4 -6 -6 q0 -12 8 -24" fill="#f3e6bf" stroke="${INK}" stroke-width="2"/>${face(100, 96, 84, 'sleepy')}`;
    case 'chimera': {
      const fluff = ring(10, 54, 100, 104)
        .map((p) => `<circle cx="${p.x}" cy="${p.y}" r="20" fill="#7c5bb0"/>`)
        .join('');
      const spikes = ring(8, 60, 100, 104)
        .map((p) => {
          const x = 100 + Math.cos(p.a) * 86;
          const y = 104 + Math.sin(p.a) * 86;
          return `<line x1="${p.x}" y1="${p.y}" x2="${x}" y2="${y}" stroke="${INK}" stroke-width="5"/><circle cx="${x}" cy="${y}" r="8" fill="#e8576d" stroke="${INK}" stroke-width="3"/>`;
        })
        .join('');
      return `${spikes}<path d="M60 160 q-10 24 -26 30 M140 160 q10 24 26 30" stroke="#2fbf71" stroke-width="9" fill="none" stroke-linecap="round"/>${fluff}<circle cx="100" cy="104" r="60" fill="#6a46a0" stroke="${INK}" stroke-width="5"/><path d="M66 56 L74 30 L88 48 L100 22 L112 48 L126 30 L134 56 Z" fill="#ffd23a" stroke="${INK}" stroke-width="4" stroke-linejoin="round"/>${face(100, 98, 92, 'evil')}<circle cx="100" cy="72" r="9" fill="#ffe14a" stroke="${INK}" stroke-width="3"/><ellipse cx="100" cy="72" rx="2.5" ry="7" fill="${INK}"/>`;
    }
  }
}

/** Стекло сосуда с жидкостью; микробы — по желанию. */
export function vessel(id: VesselId, microbes = true): string {
  const glass = 'fill="#bdf3ff" fill-opacity=".12" stroke="#9edcf0" stroke-width="5"';
  const liquid = 'fill="#36e2d0" fill-opacity=".25"';
  const bugs = (spots: readonly (readonly [number, number, number])[]) =>
    microbes ? spots.map(([x, y, k]) => place(microbe(k), x, y, 0.16)).join('') : '';
  switch (id) {
    case 'jar':
      return `<rect x="48" y="44" width="104" height="146" rx="16" ${liquid}/><rect x="40" y="26" width="120" height="166" rx="20" ${glass}/><rect x="34" y="14" width="132" height="22" rx="8" fill="#b87333" stroke="${INK}" stroke-width="4"/>${bugs(
        [
          [60, 140, 0],
          [104, 150, 1],
          [80, 110, 2],
        ],
      )}`;
    case 'tube':
      return `<path d="M80 50 L80 170 A20 20 0 0 0 120 170 L120 50 Z" ${liquid}/><path d="M76 14 L76 170 A24 24 0 0 0 124 170 L124 14" ${glass} stroke-linecap="round"/><rect x="70" y="8" width="60" height="12" rx="5" fill="#b87333" stroke="${INK}" stroke-width="3"/>${bugs(
        [
          [84, 150, 1],
          [84, 110, 0],
        ],
      )}`;
    case 'petri':
      return `<ellipse cx="100" cy="128" rx="88" ry="34" ${liquid}/><ellipse cx="100" cy="122" rx="92" ry="40" ${glass}/><path d="M8 122 L8 140 A92 40 0 0 0 192 140 L192 122" fill="none" stroke="#9edcf0" stroke-width="5"/>${bugs(
        [
          [40, 104, 2],
          [84, 114, 0],
          [130, 104, 1],
        ],
      )}`;
    case 'flask':
      return `<path d="M58 120 L26 186 L174 186 L142 120 Z" ${liquid}/><path d="M80 14 L80 76 L22 186 Q20 194 30 194 L170 194 Q180 194 178 186 L120 76 L120 14" ${glass} stroke-linejoin="round"/><rect x="72" y="8" width="56" height="12" rx="5" fill="#b87333" stroke="${INK}" stroke-width="3"/>${bugs(
        [
          [60, 150, 0],
          [110, 156, 2],
        ],
      )}`;
    case 'bioreactor':
      return `<rect x="36" y="40" width="128" height="150" rx="22" ${liquid}/><rect x="30" y="26" width="140" height="166" rx="26" ${glass}/><rect x="74" y="80" width="10" height="112" fill="#b87333"/><rect x="116" y="80" width="10" height="112" fill="#b87333"/><path d="M60 26 L60 4 M140 26 L140 4" stroke="#b87333" stroke-width="10"/><circle cx="100" cy="18" r="10" fill="#ff7a3d" stroke="${INK}" stroke-width="3"/>${bugs(
        [
          [40, 150, 0],
          [88, 140, 1],
          [134, 152, 2],
        ],
      )}`;
  }
}

/**
 * Доктор-биоинженер Кузницы (SPEC.md, 19.6): защитный фартук, гогглы, перчатки,
 * медный инструмент. Без халата, стетоскопа, зеркала на лбу и усов.
 */
export function doctor(mood: 'calm' | 'determined' | 'surprised' = 'calm'): string {
  const mouth =
    mood === 'surprised'
      ? `<ellipse cx="100" cy="98" rx="7" ry="9" fill="${INK}"/>`
      : mood === 'determined'
        ? `<path d="M88 96 q12 6 24 -2" stroke="${INK}" stroke-width="4" fill="none" stroke-linecap="round"/>`
        : `<path d="M88 94 q12 10 24 0" stroke="${INK}" stroke-width="4" fill="none" stroke-linecap="round"/>`;
  return `<path d="M40 200 Q42 132 100 126 Q158 132 160 200 Z" fill="#2f4858" stroke="${INK}" stroke-width="5"/><path d="M70 136 L70 200 L130 200 L130 136 Q100 148 70 136 Z" fill="#7a4a2a" stroke="${INK}" stroke-width="4"/><circle cx="82" cy="150" r="5" fill="#b87333"/><circle cx="118" cy="150" r="5" fill="#b87333"/><rect x="88" y="166" width="24" height="18" rx="4" fill="#5c3720" stroke="${INK}" stroke-width="3"/><path d="M150 168 l26 -40" stroke="#b87333" stroke-width="10" stroke-linecap="round"/><circle cx="178" cy="124" r="10" fill="none" stroke="#b87333" stroke-width="7"/><circle cx="150" cy="172" r="14" fill="#ff9a3d" stroke="${INK}" stroke-width="4"/><ellipse cx="100" cy="78" rx="40" ry="44" fill="#f2c9a0" stroke="${INK}" stroke-width="5"/><path d="M60 70 Q62 30 100 32 Q140 30 140 70 Q128 50 100 52 Q72 50 60 70 Z" fill="#3a2a4a" stroke="${INK}" stroke-width="4"/><rect x="56" y="58" width="88" height="10" rx="5" fill="#3a3a3a"/><circle cx="82" cy="70" r="15" fill="#8cfff0" fill-opacity=".85" stroke="#b87333" stroke-width="6"/><circle cx="118" cy="70" r="15" fill="#8cfff0" fill-opacity=".85" stroke="#b87333" stroke-width="6"/><circle cx="82" cy="72" r="5" fill="${INK}"/><circle cx="118" cy="72" r="5" fill="${INK}"/><path d="M76 64 l6 -4" stroke="#fff" stroke-width="3" stroke-linecap="round"/>${mouth}`;
}

/** «Кузница» — голос лаборатории: пульт с лампой-глазом. */
export function forge(alarm = false): string {
  const eye = alarm ? '#ff4f7b' : '#8cfff0';
  return `<path d="M30 190 L50 120 L150 120 L170 190 Z" fill="#26303f" stroke="${INK}" stroke-width="5"/><rect x="44" y="40" width="112" height="88" rx="18" fill="#33465a" stroke="${INK}" stroke-width="5"/><circle cx="100" cy="82" r="26" fill="${eye}" fill-opacity=".25" stroke="${eye}" stroke-width="5"/><circle cx="100" cy="82" r="11" fill="${eye}"/><path d="M60 150 h80 M64 166 h72" stroke="#5c7488" stroke-width="6" stroke-linecap="round"/><path d="M100 40 V16" stroke="#b87333" stroke-width="6"/><circle cx="100" cy="12" r="7" fill="${eye}"/>`;
}

/** Значки для карточек способностей, правил, ролей и эффектов. */
export function icon(id: string): string {
  const capsule = (x: number, y: number, a: string, b: string, rotate = 0) =>
    `<g transform="translate(${x} ${y}) rotate(${rotate})"><rect x="-40" y="-18" width="40" height="36" rx="16" fill="${a}" stroke="${INK}" stroke-width="4"/><rect x="0" y="-18" width="40" height="36" rx="16" fill="${b}" stroke="${INK}" stroke-width="4"/></g>`;
  switch (id) {
    case 'basics':
      return `${[0, 1, 2, 3].map((i) => `<rect x="${24 + i * 40}" y="120" width="36" height="36" rx="12" fill="${MICROBE_COLORS[0]}" stroke="${INK}" stroke-width="4"/>`).join('')}${capsule(100, 60, MICROBE_COLORS[0], MICROBE_COLORS[2])}<path d="M100 84 v22" stroke="#fff" stroke-width="5" stroke-dasharray="6 6"/>`;
    case 'energy':
      return `<rect x="20" y="80" width="160" height="40" rx="20" fill="#0e3446" stroke="#9edcf0" stroke-width="5"/><rect x="28" y="88" width="110" height="24" rx="12" fill="#36e2d0"/><path d="M110 30 L84 104 L106 104 L92 172 L134 84 L110 84 L124 30 Z" fill="#ffd23a" stroke="${INK}" stroke-width="5" stroke-linejoin="round"/>`;
    case 'ability-cryo':
    case 'effect-freeze':
      return `<circle cx="100" cy="100" r="84" fill="#cff6ff" fill-opacity=".15"/>${[0, 60, 120]
        .map(
          (a) =>
            `<g transform="rotate(${a} 100 100)"><path d="M100 24 V176 M100 50 l-18 -16 M100 50 l18 -16 M100 150 l-18 16 M100 150 l18 16" stroke="#eaffff" stroke-width="9" stroke-linecap="round" fill="none"/></g>`,
        )
        .join('')}`;
    case 'ability-antibiotic':
      return `${capsule(116, 62, '#ffffff', '#36e2d0', -30)}<g transform="translate(168 30)"><path d="M-14 0 h28 M0 -14 v28" stroke="#ff4f7b" stroke-width="9" stroke-linecap="round"/></g>${place(microbe(2), 18, 96, 0.45)}<path d="M40 120 l60 60 M100 120 l-60 60" stroke="#ff4f7b" stroke-width="9" stroke-linecap="round"/>`;
    case 'ability-scanner':
      return `<rect x="24" y="40" width="152" height="110" rx="16" fill="#0e3446" stroke="#9edcf0" stroke-width="6"/>${capsule(62, 95, MICROBE_COLORS[0], MICROBE_COLORS[1])}${capsule(140, 95, MICROBE_COLORS[2], MICROBE_COLORS[0])}<path d="M70 176 h60" stroke="#9edcf0" stroke-width="8" stroke-linecap="round"/>`;
    case 'ability-immunity':
      return `<path d="M100 16 L170 44 Q172 132 100 186 Q28 132 30 44 Z" fill="#36e2d0" fill-opacity=".3" stroke="#8cfff0" stroke-width="7" stroke-linejoin="round"/><path d="M66 100 l24 24 l46 -50" stroke="#eaffff" stroke-width="12" fill="none" stroke-linecap="round" stroke-linejoin="round"/>`;
    case 'ability-fever':
    case 'effect-storm':
      return `<rect x="84" y="20" width="32" height="120" rx="16" fill="#eaffff" stroke="${INK}" stroke-width="5"/><rect x="92" y="60" width="16" height="84" fill="#ff4f7b"/><circle cx="100" cy="156" r="28" fill="#ff4f7b" stroke="${INK}" stroke-width="5"/><path d="M150 40 q14 16 0 32 q-14 16 0 32 M40 60 q-12 14 0 28" stroke="#ff7a3d" stroke-width="7" fill="none" stroke-linecap="round"/>`;
    case 'attack-infect':
      return `${place(microbe(0), 10, 70, 0.4)}${place(microbe(1), 70, 110, 0.4)}<path d="M110 70 C140 40 170 50 180 80" stroke="#ff4f7b" stroke-width="8" fill="none" stroke-linecap="round"/><path d="M182 62 l0 22 l-22 -4" stroke="#ff4f7b" stroke-width="8" fill="none" stroke-linecap="round" stroke-linejoin="round"/>`;
    case 'role-pilot':
      return `${capsule(100, 60, MICROBE_COLORS[1], MICROBE_COLORS[2])}<rect x="50" y="130" width="100" height="44" rx="14" fill="#33465a" stroke="${INK}" stroke-width="5"/><path d="M100 130 V100" stroke="${INK}" stroke-width="8"/><circle cx="100" cy="96" r="14" fill="#ff4f7b" stroke="${INK}" stroke-width="4"/>`;
    case 'role-strategist':
      return `${capsule(54, 70, MICROBE_COLORS[0], MICROBE_COLORS[1])}${capsule(146, 70, MICROBE_COLORS[2], MICROBE_COLORS[2])}<path d="M146 98 v40 l-10 -10 m10 10 l10 -10" stroke="#8cfff0" stroke-width="7" fill="none" stroke-linecap="round"/><rect x="40" y="150" width="120" height="30" rx="10" fill="#0e3446" stroke="#9edcf0" stroke-width="4"/>`;
    case 'effect-fog':
    case 'boss-fog':
      return `${place(vessel('petri'), 0, 0)}${[40, 90, 140].map((x) => `<ellipse cx="${x}" cy="100" rx="40" ry="26" fill="#0a2a1c" fill-opacity=".85"/>`).join('')}<circle cx="70" cy="70" r="8" fill="#2fbf71"/><circle cx="130" cy="64" r="6" fill="#2fbf71"/>`;
    case 'effect-sleep':
    case 'boss-latency':
      return `${place(boss('tuberkul'), 30, 40, 0.7)}<text x="134" y="70" font-family="system-ui, sans-serif" font-weight="800" font-size="40" fill="#8cfff0">Z</text><text x="160" y="40" font-family="system-ui, sans-serif" font-weight="800" font-size="28" fill="#8cfff0">z</text>`;
    case 'boss-replicate':
      return `${place(microbe(0), 20, 20, 0.45)}${place(microbe(0), 110, 20, 0.45)}${[
        [40, 130],
        [130, 130],
      ]
        .map(
          ([x, y]) =>
            `<rect x="${(x ?? 0) - 4}" y="${(y ?? 0) - 4}" width="48" height="48" rx="10" fill="none" stroke="#ffd23a" stroke-width="5" stroke-dasharray="10 6"/>`,
        )
        .join('')}`;
    case 'boss-raise-floor':
      return `<rect x="20" y="20" width="160" height="160" rx="16" fill="#0e3446" stroke="#9edcf0" stroke-width="5"/>${[0, 1, 2, 3].map((i) => place(microbe(i % 3), 26 + i * 38, 136, 0.18)).join('')}<path d="M100 120 V50 M76 74 l24 -24 l24 24" stroke="#ffd23a" stroke-width="10" fill="none" stroke-linecap="round" stroke-linejoin="round"/>`;
    case 'boss-mutate':
      return `${place(microbe(0), 0, 50, 0.5)}<path d="M98 100 h30 m-12 -14 l14 14 l-14 14" stroke="#ffd23a" stroke-width="8" fill="none" stroke-linecap="round" stroke-linejoin="round"/>${place(microbe(1), 110, 50, 0.45)}`;
    case 'boss-storm':
      return `<path d="M40 90 Q40 50 80 52 Q96 20 130 36 Q170 36 164 80 Q190 96 160 116 L50 116 Q20 110 40 90 Z" fill="#5c6b80" stroke="${INK}" stroke-width="5"/><path d="M104 116 L84 158 L108 156 L94 194" stroke="#ffd23a" stroke-width="9" fill="none" stroke-linejoin="round" stroke-linecap="round"/>${[60, 140].map((x) => `<path d="M${x} 130 v30 m-10 -10 l10 10 l10 -10" stroke="#9edcf0" stroke-width="6" fill="none" stroke-linecap="round"/>`).join('')}`;
    case 'boss-regrow':
      return `<path d="M60 190 q-30 -60 10 -110 q30 -40 70 -30" stroke="#2fbf71" stroke-width="16" fill="none" stroke-linecap="round"/><path d="M130 30 l24 10 l-14 22" stroke="#8cfff0" stroke-width="8" fill="none" stroke-linecap="round" stroke-linejoin="round"/><path d="M150 120 h30 M165 105 v30" stroke="#36e2d0" stroke-width="9" stroke-linecap="round"/>`;
    default:
      return `<circle cx="100" cy="100" r="70" fill="#0e3446" stroke="#9edcf0" stroke-width="5"/><text x="100" y="124" text-anchor="middle" font-family="system-ui, sans-serif" font-weight="800" font-size="70" fill="#8cfff0">?</text>`;
  }
}
